import { useState } from "react";

import { styled } from "@mui/material/styles";
import { Box, Stepper, Step, StepLabel, Typography, Fade } from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import Page from "src/components/Page";
import Logo from "src/components/Logo";
import { onboardingValidationSchema } from "src/util/validationSchemas";
import AboutYouStep from "./AboutYouStep";
import YourNeedsStep from "./YourNeedsStep";
import VerifyEmailStep from "./VerifyEmailStep";
import { onboardingService } from "src/services";
import { saveOnboardingData } from "src/util/onboardingStorage";

import "src/pages/pages.css";
import ThemeModeToggle from "src/components/ThemeModeToggle";

const RootStyle = styled(Page)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  backgroundColor: "#f6f7ff",
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

const STEPS = ["About You", "Your Needs", "Verify Email"];
const TOTAL_STEPS = STEPS.length;

// TEMPORARY: the send-verification-email endpoint doesn't exist yet. Treat
// every call as successful (response === true) so the UI flow can be built
// and tested end-to-end. Replace this with the real API call once it's
// ready — the call sites (onSubmit below, and Resend in VerifyEmailStep)
// won't need to change, only this implementation.
async function sendVerificationEmail(email) {
  // TODO: replace with real API call, e.g.:
  // const response = await api.post("/auth/send-verification-email", { email });
  // return response.success;
  return Promise.resolve(true);
}

export default function Signup() {
  const [activeStep, setActiveStep] = useState(0);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      country: "",
      companyName: "",
      useCase: "home",
      chargingSetup: "",
      chargerCount: "",
      acceptedTerms: false,
    },
    validationSchema: onboardingValidationSchema,
    onSubmit: () => {},
  });

 const submitRegistration = async () => {
  formik.setSubmitting(true);
  formik.setStatus(undefined);

  const {
    firstName,
    lastName,
    companyName,
    email,
    useCase,
    chargingSetup,
    chargerCount,
    acceptedTerms,
    country,
  } = formik.values;

  const payload = {
    companyInfo: {
      firstName,
      lastName,
      email,
      country,
      name: companyName,
      useCase,
      chargingSetup,
      chargerCount,
      acceptedTerms,
    },
  };

  try {
    const response = await onboardingService.submitOnboarding(payload);

    saveOnboardingData({
      userId: response?.data?.userId,
      firstName,
      lastName,
      email,
      country,
      companyName,
      useCase,
      chargingSetup,
      chargerCount,
      acceptedTerms,
    });

    setActiveStep(2);
  } catch (error) {
    console.error("Onboarding error:", error);
    formik.setStatus(
      error?.response?.data?.message ??
        "Something went wrong sending the verification email. Please try again."
    );
  } finally {
    formik.setSubmitting(false);
  }
};

  const validateStepFields = async (fields) => {
    try {
      await onboardingValidationSchema.pick(fields).validate(formik.values, {
        abortEarly: false,
      });
      return true;
    } catch (err) {
      const errors = {};
      const touchedFields = {};
      (err.inner ?? []).forEach((e) => {
        errors[e.path] = e.message;
        touchedFields[e.path] = true;
      });
      formik.setErrors({ ...formik.errors, ...errors });
      formik.setTouched({ ...formik.touched, ...touchedFields });
      return false;
    }
  };

const handleAboutYouNext = async () => {
  if (await validateStepFields(["firstName", "lastName", "email", "country"])) {
    setActiveStep(1);
  }
};

const handleYourNeedsSubmit = async () => {
  if (!formik.values.acceptedTerms) {
    formik.setFieldError("acceptedTerms", "Please accept the terms to continue");
    formik.setFieldTouched("acceptedTerms", true);
    return;
  }
  const fields = ["useCase", "chargingSetup", "acceptedTerms"];
  if (formik.values.useCase === "business") {
    fields.push("companyName", "chargerCount");
  }
  if (await validateStepFields(fields)) {
    submitRegistration();
  }
};

  return (
    <RootStyle title="Onboarding | Smart-CMS">
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: { xs: "40px 16px", md: "60px 32px" },
        }}
      >
        <Box
  sx={{
    position: "absolute",
    top: { xs: 16, md: 24 },
    right: { xs: 16, md: 24 },
    zIndex: 10,
  }}
>
  <ThemeModeToggle />
</Box>
        <Box
          sx={{
            width: "100%",
            maxWidth: 560,
            backgroundColor: "background.paper",
            borderRadius: "15px",
            boxShadow: "0 30px 70px rgba(34, 45, 90, 0.12)",
            p: { xs: 3, md: 4 },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Logo />
          </Box>

          {activeStep < TOTAL_STEPS - 1 && (
            <Box sx={{ mb: 4 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              <Typography
                variant="caption"
                color="text.secondary"
                align="center"
                sx={{ display: "block", mt: 1 }}
              >
                Step {activeStep + 1} of {TOTAL_STEPS}
              </Typography>
            </Box>
          )}

          <Fade in timeout={300} key={activeStep}>
            <Box>
              {activeStep === 0 && (
                <FormikProvider value={formik}>
                  <AboutYouStep onNext={handleAboutYouNext} />
                </FormikProvider>
              )}
              {activeStep === 1 && (
                <FormikProvider value={formik}>
                  <YourNeedsStep onBack={() => setActiveStep(0)} onSubmit={handleYourNeedsSubmit} />
                </FormikProvider>
              )}
              {activeStep === 2 && (
                <VerifyEmailStep
                  email={formik.values.email}
                  onResend={sendVerificationEmail}
                  onEditEmail={() => setActiveStep(0)}
                />
              )}
            </Box>
          </Fade>
        </Box>
      </Box>
    </RootStyle>
  );
}